import React, { useState, useCallback, useMemo } from "react";

import "./TimeInput.scss";
import useThemeService from "../../services/theme-service/ThemeService";
import { ArrowLeft, ArrowRight } from "../svg/svgImageList";
import Button from "../button/Button";

interface TimeInputProps {
  name: string;
  onChange: (number: number) => void;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  showDefault?: boolean;
  placeholder: string;
}

export default function TimeInput({
  name,
  onChange,
  value,
  min,
  max,
  disabled,
  showDefault = true,
  placeholder,
}: TimeInputProps) {
  const { themeClass } = useThemeService();
  const [number, setNumber] = useState<number>(value);

  const displayNumber = useMemo(() => {
    if (Number.isNaN(number)) return "";
    if (number.toString().length === 1) return `0${number}`;
    return number;
  }, [number]);

  var increase = useCallback(() => {
    if (disabled) return;

    setNumber((number: number) => {
      const increasedNumber: number = number + 1;
      onChange(increasedNumber);
      return increasedNumber;
    });
  }, [disabled, onChange]);

  var decrease = useCallback(() => {
    if (disabled) return;

    setNumber((number: number) => {
      const decreasedNumber: number = number - 1;
      onChange(decreasedNumber);
      return decreasedNumber;
    });
  }, [disabled, onChange]);

  const handleUp = useCallback(() => {
    if (number < max) increase();
  }, [increase, max, number]);

  const handleDown = useCallback(() => {
    if (number > min) decrease();
  }, [decrease, min, number]);

  const handleInputChange = useCallback(
    (value: any) => {
      if (disabled) return;

      if (!value) setNumber(min);

      if (value <= max) setNumber(parseInt(value));
    },
    [disabled, max, min]
  );

  const handleInputBlur = useCallback(
    (value: any) => {
      if (Number.isNaN(number)) {
        onChange(min);
      } else {
        onChange(parseInt(value));
      }
    },
    [min, number, onChange]
  );

  return (
    <div className={disabled ? "time-input-container disabled" : "time-input-container"}>
      <input
        name={name}
        value={showDefault ? displayNumber : undefined}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={(e) => handleInputBlur(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
      <div className="buttons-container">
        <Button
          icon={{
            svg: ArrowRight,
            className: themeClass(["up-icon"]),
          }}
          className={themeClass(["button"])}
          onClick={handleUp}
        />
        <Button
          icon={{
            svg: ArrowLeft,
            className: themeClass(["down-icon"]),
          }}
          className={themeClass(["button"])}
          onClick={handleDown}
        />
      </div>
    </div>
  );
}
